import { useCallback, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { Card, MonoMeta, SummaryStripBase } from "@/design/primitives";
import { ProjectDetail } from "./ProjectDetail";
import { WorkingRow } from "./WorkingRow";

const SUMMARY_SEPARATOR = " · ";

// Council round 2026-10-21: ONE count language — three numbers only:
// need your word / running / parked.
function buildProjectsSummary(cards) {
  const working = cards.filter((c) => ["EXECUTE", "TEST"].includes(c.rawState)).length;
  const needsYou = cards.filter((c) => needsJeff(c)).length;
  const parked = cards.filter((c) => ["ARCHIVED", "TABLED"].includes(c.rawState)).length;
  const parts = [];
  if (needsYou) parts.push({ key: "needs", text: `${needsYou} need your word`, tone: "amber" });
  if (working) parts.push({ key: "working", text: `${working} running`, tone: "neutral" });
  if (parked) parts.push({ key: "parked", text: `${parked} parked`, tone: "neutral" });
  if (!parts.length) parts.push({ key: "quiet", text: "the board is quiet — nothing in motion", tone: "green" });
  const tone = needsYou ? "amber" : "neutral";
  return { parts, tone, text: parts.map((p) => p.text).join(SUMMARY_SEPARATOR) };
}

// One definition of "needs Jeff" — used by the summary, the section, the deck.
function needsJeff(c) {
  return (
    ["INTAKE", "PLAN", "CLOSE"].includes(c.rawState) ||
    (c.resume?.blockers || []).some((b) => typeof b === "string" && b.toLowerCase().includes("jeff"))
  );
}

const StalenessBanner = ({ clock }) => {
  if (clock.fresh) {
    return (
      <MonoMeta as="p" data-testid="projects-as-of" className="shrink-0 whitespace-nowrap pt-1 text-os-ink">
        as of {clock.stampText}
      </MonoMeta>
    );
  }
  return (
    <Card
      as="section"
      data-testid="projects-stale-banner"
      data-dead={clock.dead}
      aria-label="Board freshness warning"
      className="px-5 py-3"
    >
      <p className={cn("text-[0.875rem] leading-[1.45]", clock.dead ? "text-os-red" : "text-os-amber")}>
        {clock.dead
          ? "Board is not checking right now — Kit needs to look at the updater"
          : `Board info is from ${clock.stampText} — Kit's check-ins keep it current`}
      </p>
    </Card>
  );
};

export const ProjectsBoard = ({ cards, clock, variant = "desktop", initialOpenSlug = null, board = null }) => {
  const [openSlug, setOpenSlug] = useState(initialOpenSlug);
  const [showParked, setShowParked] = useState(false);
  const desktop = variant === "desktop";

  const summary = useMemo(() => buildProjectsSummary(cards), [cards]);

  // Sections per spec: In motion (rows), Set aside (folded). Needs-you lives in
  // the deck above the board — it doesn't repeat here.
  const working = cards.filter((c) => ["EXECUTE", "TEST"].includes(c.rawState));
  const parked = cards.filter((c) => ["ARCHIVED", "TABLED"].includes(c.rawState));
  const openItem = cards.find((c) => c.slug === openSlug);
  const close = useCallback(() => setOpenSlug(null), []);

  return (
    <div data-testid="projects-board" data-variant={variant} className="w-full font-inter text-os-ink">
      <div className={desktop ? "mx-auto flex max-w-[100rem] flex-col gap-4 px-10 py-6" : "flex flex-col gap-3 px-4 pb-8 pt-4"}>
        <SummaryStripBase
          {...summary}
          separator={SUMMARY_SEPARATOR}
          size={desktop ? "lg" : "sm"}
          meta={<StalenessBanner clock={clock} />}
        />
        {working.length > 0 && (
          <section data-testid="projects-section-working">
            <h2 className="mb-2 text-[0.8125rem] font-semibold uppercase tracking-[0.08em] text-os-ink-3">
              In motion — {working.length}
            </h2>
            <div className="flex flex-col gap-2">
              {working.map((card) => (
                <WorkingRow key={card.slug} card={card} onOpen={setOpenSlug} />
              ))}
            </div>
          </section>
        )}
        {parked.length > 0 && (
          <section data-testid="projects-section-parked">
            <button
              type="button"
              data-testid="projects-toggle-parked"
              aria-expanded={showParked}
              onClick={() => setShowParked((s) => !s)}
              className="mb-2 flex items-center gap-2 self-start font-mono text-[12px] text-os-ink-3 underline-offset-4 hover:text-os-ink hover:underline"
            >
              <span className={cn("inline-block transition-transform", showParked && "rotate-90")}>▶</span>
              Set aside ({parked.length})
            </button>
            {showParked && (
              <div className="flex flex-col gap-2">
                {parked.map((card) => (
                  <WorkingRow key={card.slug} card={card} onOpen={setOpenSlug} />
                ))}
              </div>
            )}
          </section>
        )}
      </div>
      {openItem && (
        <ProjectDetail
          card={openItem}
          variant={variant}
          onClose={close}
          editor={board?.live ? { board, item: board.items.find((r) => r.slug === openSlug) } : null}
        />
      )}
    </div>
  );
};